import { CiaFilters } from '@context/ProjectFilters/types';
import {
  ModelFilterCategory,
  ModelFiltersStore,
  ModelFilterState,
} from '@store/zustand/model-filters/types';
import { chipValueFits, summarizeSelection } from '@utils/filterSummary';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * The three categories that live inside `ciaFilters` rather than as a flat
 * list. Their names double as the keys of `CiaFilters`, which is what lets
 * `setCategory` and `clearCategory` route by name.
 */
const CIA_CATEGORIES: ReadonlyArray<keyof CiaFilters> = [
  'assurance_level',
  'coverage',
  'applicability',
];

/**
 * Whether a category's selection lives inside `ciaFilters`, keyed by criteria,
 * rather than as a flat list on the state.
 */
export const isCiaCategory = (
  category: ModelFilterCategory,
): category is keyof CiaFilters =>
  CIA_CATEGORIES.includes(category as keyof CiaFilters);

const emptyState = (): ModelFilterState => ({
  domains: [],
  validationStatus: [],
  reviewStatus: [],
  operationalStatuses: [],
  operatingModelStatuses: [],
  partners: [],
  ciaFilters: { assurance_level: {}, coverage: {}, applicability: {} },
});

const pickState = (store: ModelFiltersStore): ModelFilterState => ({
  domains: store.domains,
  validationStatus: store.validationStatus,
  reviewStatus: store.reviewStatus,
  operationalStatuses: store.operationalStatuses,
  operatingModelStatuses: store.operatingModelStatuses,
  partners: store.partners,
  ciaFilters: store.ciaFilters,
});

const hasCiaAnswers = (field: Record<string, string[]> | undefined) =>
  Object.values(field ?? {}).some((answers) => answers?.length);

/**
 * The selection that writing `values` into `category` produces, without
 * writing it. `setCategory` applies exactly this, and the popover evaluates
 * the shared predicate against it before the write, so an open drawer can be
 * closed before the canvas hides its entity. A CIA category needs the
 * `criteriaId` the values belong to; without one there is nowhere to put
 * them, and the state comes back as it was, by identity.
 */
export const withCategorySelection = (
  state: ModelFilterState,
  category: ModelFilterCategory,
  values: string[],
  criteriaId?: string,
): ModelFilterState => {
  if (!isCiaCategory(category)) {
    return { ...state, [category]: values };
  }

  if (!criteriaId) {
    return state;
  }

  return {
    ...state,
    ciaFilters: {
      ...state.ciaFilters,
      [category]: { ...state.ciaFilters[category], [criteriaId]: values },
    },
  };
};

/**
 * How many categories currently carry a selection — the AC4.3.1 badge value.
 *
 * Counts categories, not values: two Scope values give "1". The three CIA
 * rows each count separately because AC3.1 lists them as three rows, so a
 * selection under `coverage` alone reads "1" rather than folding into one CIA
 * category. A CIA criteria whose answer list is empty does not count.
 */
export const activeCategoryCount = (state: ModelFilterState): number => {
  const flat = [
    state.domains,
    state.validationStatus,
    state.reviewStatus,
    state.operationalStatuses,
    state.operatingModelStatuses,
    state.partners,
  ].filter((values) => values.length > 0).length;

  const cia = CIA_CATEGORIES.filter((category) =>
    hasCiaAnswers(state.ciaFilters[category]),
  ).length;

  return flat + cia;
};

/**
 * AC3.2's category-row summary: "Any", the single name, both names, or
 * "N selected". A value with no label falls back to the raw value rather than
 * disappearing from the summary.
 *
 * Whether two names are shown joined is measured against the applied-filter
 * chip's 240px value area beside `categoryLabel`, not estimated — the chip,
 * the root category row and the +N popover row all call this, so the three
 * texts cannot disagree. `fits` is injectable for tests.
 */
export const categorySummary = (
  values: string[],
  labels: Record<string, string>,
  categoryLabel: string,
  fits: (categoryLabel: string, text: string) => boolean = chipValueFits,
): string => {
  if (!values.length) {
    return 'Any';
  }

  const names = values.map((value) => labels[value] ?? value);

  return summarizeSelection(names, (text) => fits(categoryLabel, text));
};

/**
 * The values selected under one category, flattened across CIA criteria so
 * every category answers in the same shape regardless of how it is stored.
 */
export const categoryValues = (
  state: ModelFilterState,
  category: ModelFilterCategory,
): string[] => {
  if (isCiaCategory(category)) {
    return Object.values(state.ciaFilters[category] ?? {}).flat();
  }

  return state[category] ?? [];
};

/**
 * The model-filter selection, keyed by project or scenario.
 *
 * The live selection sits at the top level so selectors stay flat; `setScope`
 * parks it under `buckets[scopeKey]` and restores whichever bucket the new
 * key owns, so a scenario keeps its own filters apart from its parent project
 * (AC7.d.i). `clearAll` empties the live selection only — other buckets are
 * untouched, and so is the key; `reset` returns the whole store to its initial
 * state, buckets included, so nothing survives a navigation out of the project
 * workspace (AC7.d.iv). A selection made while `scopeKey` is `null` belongs
 * to no bucket, so the first `setScope` drops it rather than parking it: set
 * the key before the popover can take input.
 */
const useModelFiltersStore = create<ModelFiltersStore>((set, get) => ({
  ...emptyState(),
  scopeKey: null,
  buckets: {},

  setCategory: (category, values, criteriaId) => {
    const current = pickState(get());
    const next = withCategorySelection(current, category, values, criteriaId);

    if (next !== current) {
      set(next);
    }
  },

  setCiaFilters: (ciaFilters) => set({ ciaFilters }),

  clearCategory: (category) => {
    if (isCiaCategory(category)) {
      const { ciaFilters } = get();

      set({ ciaFilters: { ...ciaFilters, [category]: {} } });

      return;
    }

    set({ [category]: [] } as Partial<ModelFiltersStore>);
  },

  clearAll: () => set(emptyState()),

  reset: () => set({ ...emptyState(), scopeKey: null, buckets: {} }),

  setScope: (key) => {
    const store = get();

    if (store.scopeKey === key) {
      return;
    }

    const buckets = store.scopeKey
      ? { ...store.buckets, [store.scopeKey]: pickState(store) }
      : store.buckets;

    const restored = key && buckets[key] ? buckets[key] : emptyState();

    set({ ...restored, scopeKey: key, buckets });
  },
}));

export const useModelFilters = () =>
  useModelFiltersStore(useShallow(pickState));

export const useModelFilterActions = () =>
  useModelFiltersStore(
    useShallow(
      ({
        setCategory,
        setCiaFilters,
        clearCategory,
        clearAll,
        reset,
        setScope,
      }) => ({
        setCategory,
        setCiaFilters,
        clearCategory,
        clearAll,
        reset,
        setScope,
      }),
    ),
  );

export const getModelFiltersState = () => useModelFiltersStore.getState();

export default useModelFiltersStore;
